import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Crown, Loader2, Video, Swords, Wrench, Heart, Bookmark, Trophy, Lock } from "lucide-react";

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api.get("/analytics").then(({ data }) => setData(data)).catch((e) => {
      if (e?.response?.status === 402) setLocked(true);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  if (locked || !user?.is_pro) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
        <Lock className="mb-4 h-10 w-10 text-primary" />
        <h1 className="font-display text-4xl tracking-wider">Analytics is a Pro feature</h1>
        <p className="mt-2 max-w-md text-foreground/60">Upgrade to Creator Pro to unlock your full analytics dashboard.</p>
        <Button asChild className="mt-6 rounded-sm bg-primary font-bold uppercase tracking-widest"><Link to="/pricing"><Crown className="mr-2 h-4 w-4" /> Upgrade</Link></Button>
      </div>
    );
  }

  if (!data) return null;
  const maxDay = Math.max(1, ...data.last_7_days.map((d) => d.count));

  return (
    <div data-testid="analytics-page" className="mx-auto max-w-5xl px-6 py-14 md:px-10">
      <div className="mb-3 flex items-center gap-2 text-primary"><Crown className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-[0.25em]">Pro Analytics</span></div>
      <h1 className="font-display text-5xl tracking-wider md:text-6xl">Your <span className="text-primary">numbers</span></h1>

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat icon={Video} label="Total Generations" value={data.total_generations} />
        <Stat icon={Bookmark} label="Saved Ideas" value={data.saved_count} />
        <Stat icon={Heart} label="Likes Received" value={data.likes_received} />
        <Stat icon={Trophy} label="Creator Score" value={data.score} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3 border border-border bg-card p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-foreground/60">Generations · Last 7 Days</p>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.last_7_days}>
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "rgba(220,38,38,0.08)" }} contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 2, color: "#fff" }} />
                <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                  {data.last_7_days.map((d) => <Cell key={d.date} fill={d.count === maxDay ? "hsl(0 84% 45%)" : "hsl(0 60% 30%)"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 border border-border bg-card p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-foreground/60">By Generator</p>
          <div className="mt-4 space-y-4">
            <KindRow icon={Video} label="Video Ideas" value={data.by_kind.video} total={data.total_generations} />
            <KindRow icon={Swords} label="Challenges" value={data.by_kind.challenge} total={data.total_generations} />
            <KindRow icon={Wrench} label="Builds" value={data.by_kind.build} total={data.total_generations} />
          </div>
        </div>
      </div>

      {data.top_posts.length > 0 && (
        <div className="mt-6 border border-border bg-card p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-foreground/60">Top Posts</p>
          <div className="mt-4 space-y-2">
            {data.top_posts.map((p) => (
              <div key={`${p.type}-${p.title}`} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0">
                <span className="truncate text-sm text-foreground/80">{p.title}</span>
                <span className="flex shrink-0 items-center gap-1.5 text-sm text-primary"><Heart className="h-4 w-4 fill-primary" />{p.likes}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="border border-border bg-card p-5 text-center">
    <Icon className="mx-auto h-5 w-5 text-primary" />
    <p className="mt-2 font-display text-3xl">{value}</p>
    <p className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">{label}</p>
  </div>
);

const KindRow = ({ icon: Icon, label, value, total }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-foreground/70"><Icon className="h-4 w-4 text-primary" />{label}</span>
        <span className="font-bold">{value}</span>
      </div>
      <div className="h-2 w-full bg-background"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};
